import type {Draft} from 'immer';

import type {RuntimeEvent} from '../runtime/events';
import {assistantMessageId} from '../runtime/turn';
import {addMessageNode, findToolCallPart} from './messageTree';
import type {
  ContentPart,
  MessageNode,
  RunOutcome,
  Thread,
  ToolCallPart,
} from './thread';

/** applyRuntimeEventToThread applies a runtime event to a thread draft. */
export function applyRuntimeEventToThread(
  thread: Draft<Thread>,
  event: RuntimeEvent,
  now: number
): void {
  switch (event.type) {
    case 'turn-created': {
      if (!thread.rootTurnId) {
        thread.rootTurnId = event.rootTurnId;
      }
      // Adopt the server's turn-creation time so lastActivityAt is on the
      // server clock right away (consistent with mergeThreadSummaries). Math.max
      // keeps it monotonic vs. the optimistic client-send bump in addUserMessage.
      thread.lastActivityAt = Math.max(
        thread.lastActivityAt,
        event.lastActivityAt
      );
      thread.latestTurnId = event.turnId;
      break;
    }
    case 'user-message': {
      const node: MessageNode = {
        id: event.messageId,
        role: 'user',
        content: event.content,
        createdAt: now,
      };
      addMessageNode(
        thread.messageTree,
        thread.messageTree.currentNodeId,
        node
      );
      break;
    }
    case 'run-started': {
      thread.status = 'running';
      thread.currentRunId = event.runId;
      thread.error = null;
      // A new run starts fresh; drop any lingering cancel intent.
      thread.cancelRequested = false;
      // A new run answers (or abandons) any prior questions; clear them so the
      // composer reverts to its normal state for this turn.
      thread.pendingQuestions = null;
      // Likewise a new run resolves (grants/denies) any pending network access
      // request. Active grants are tracked separately in activeNetworkDomains,
      // refreshed from each completed turn's resolved permissions.
      thread.pendingNetworkAccessRequest = null;
      break;
    }
    case 'agent-questions': {
      thread.pendingQuestions = event.questions;
      break;
    }
    case 'network-access-request': {
      thread.pendingNetworkAccessRequest = event.request;
      break;
    }
    case 'network-permissions': {
      thread.activeNetworkDomains = event.domains;
      break;
    }
    case 'run-finished': {
      finalizeInProgressParts(thread);
      thread.runOutcomes[event.runId] = 'finished';
      if (event.runId === thread.currentRunId) {
        settleTurnOutcome(thread, 'finished');
        thread.status = 'idle';
        thread.currentRunId = null;
        thread.currentMessageId = null;
        thread.cancelRequested = false;
      }
      break;
    }
    case 'run-cancelled': {
      finalizeInProgressParts(thread);
      thread.runOutcomes[event.runId] = 'cancelled';
      if (event.runId === thread.currentRunId) {
        settleTurnOutcome(thread, 'cancelled');
        ensureCancelledResponseNode(thread, now);
        thread.status = 'idle';
        thread.currentRunId = null;
        thread.currentMessageId = null;
        thread.cancelRequested = false;
      }
      break;
    }
    case 'run-error': {
      finalizeInProgressParts(thread);
      thread.runOutcomes[event.runId] = 'errored';
      if (event.runId === thread.currentRunId) {
        settleTurnOutcome(thread, 'errored');
        thread.status = 'error';
        thread.error = event.message;
        thread.currentRunId = null;
        thread.currentMessageId = null;
        thread.cancelRequested = false;
      }
      break;
    }

    case 'text-message-start':
      handleStreamingStart(thread, 'text', event.messageId, now);
      break;
    case 'text-message-content':
      handleStreamingDelta(thread, 'text', event.messageId, event.delta);
      break;
    case 'text-message-end':
      handleStreamingEnd(thread, 'text', event.messageId);
      break;

    case 'reasoning-start':
      handleStreamingStart(thread, 'thinking', event.messageId, now);
      break;
    case 'reasoning-content':
      handleStreamingDelta(thread, 'thinking', event.messageId, event.delta);
      break;
    case 'reasoning-end':
      handleStreamingEnd(thread, 'thinking', event.messageId);
      break;

    case 'tool-call-start': {
      const msg = ensureCurrentAssistantMessage(thread, event.messageId, now);
      msg.content.push({
        type: 'tool-call',
        toolCallId: event.toolCallId,
        toolName: event.toolName,
        argsText: '',
        args: null,
        status: 'pending',
      } as Draft<ContentPart>);
      break;
    }
    case 'tool-call-args': {
      const part = findToolCallPartInThread(thread, event.toolCallId);
      if (part) {
        part.argsText += event.delta;
      }
      break;
    }
    case 'tool-call-end': {
      const part = findToolCallPartInThread(thread, event.toolCallId);
      if (part) {
        try {
          part.args = JSON.parse(part.argsText);
        } catch {
          part.args = null;
        }
        part.status = 'executing';
      }
      break;
    }
    case 'tool-call-result': {
      const part = findToolCallPartInThread(thread, event.toolCallId);
      if (part) {
        part.result = event.content;
        part.isError = event.isError;
        part.status = event.isError ? 'error' : 'done';
      }
      break;
    }
  }
}

/**
 * settleTurnOutcome records the current turn's terminal outcome keyed by turnId
 * (from `latestTurnId`) so a settled turn can render its outcome consistently
 * live and after reload. No-op until the turn id is known.
 */
function settleTurnOutcome(thread: Draft<Thread>, outcome: RunOutcome): void {
  if (thread.latestTurnId) {
    thread.turnOutcomes[thread.latestTurnId] = outcome;
  }
}

/**
 * ensureCancelledResponseNode guarantees the cancelled turn has an assistant
 * message node, so a cancel that landed before any streamed content still
 * renders its stopped-response marker. No-op once the node exists or the turn
 * id is unknown.
 */
function ensureCancelledResponseNode(thread: Draft<Thread>, now: number): void {
  if (!thread.latestTurnId) {
    return;
  }
  ensureCurrentAssistantMessage(
    thread,
    assistantMessageId(thread.latestTurnId),
    now
  );
}

/**
 * ensureCurrentAssistantMessage sets the thread's current message to an assistant node
 * with `messageId`, creating and appending a new node if it differs from the
 * current message.
 */
function ensureCurrentAssistantMessage(
  thread: Draft<Thread>,
  messageId: string,
  now: number
): Draft<MessageNode> {
  if (thread.currentMessageId !== messageId) {
    const node: MessageNode = {
      id: messageId,
      role: 'assistant',
      content: [],
      createdAt: now,
    };

    addMessageNode(thread.messageTree, thread.messageTree.currentNodeId, node);
    thread.currentMessageId = messageId;
  }

  return thread.messageTree.nodes[messageId];
}

/**
 * finalizeInProgressParts finalizes any content parts still marked as in-
 * progress on the current message. Call when a run ends (finished or error) to
 * prevent parts from being stuck in a loading state.
 *
 * NOTE: Text and thinking parts transition to 'complete' because their partial
 * content is still valid and displayable. Tool-call parts transition to 'error'
 * because a pending or executing tool call can never produce a result once the
 * run has ended — the UI should treat them as failed.
 */
function finalizeInProgressParts(thread: Draft<Thread>): void {
  if (!thread.currentMessageId) {
    return;
  }

  const msg = thread.messageTree.nodes[thread.currentMessageId];
  if (!msg) {
    return;
  }

  for (const part of msg.content) {
    if (part.type === 'text' || part.type === 'thinking') {
      if (part.status === 'in-progress') {
        part.status = 'complete';
      }
    } else if (part.type === 'tool-call') {
      if (part.status === 'pending' || part.status === 'executing') {
        part.status = 'error';
        part.result = part.result ?? 'Interrupted';
        part.isError = true;
      }
    }
  }
}

function findLastPartOfType<T extends ContentPart['type']>(
  content: Draft<ContentPart>[],
  type: T
): Draft<Extract<ContentPart, {type: T}>> | undefined {
  for (let i = content.length - 1; i >= 0; i--) {
    if (content[i].type === type) {
      return content[i] as Draft<Extract<ContentPart, {type: T}>>;
    }
  }

  return undefined;
}

function findToolCallPartInThread(
  thread: Draft<Thread>,
  toolCallId: string
): Draft<ToolCallPart> | undefined {
  const location = findToolCallPart(
    thread.messageTree,
    toolCallId,
    thread.currentMessageId
  );
  if (!location) {
    return undefined;
  }

  return thread.messageTree.nodes[location.nodeId].content[
    location.partIndex
  ] as Draft<ToolCallPart>;
}

function handleStreamingStart(
  thread: Draft<Thread>,
  partType: 'text' | 'thinking',
  messageId: string,
  now: number
): void {
  const msg = ensureCurrentAssistantMessage(thread, messageId, now);
  msg.content.push({
    type: partType,
    text: '',
    status: 'in-progress',
  } as Draft<ContentPart>);
}

function handleStreamingDelta(
  thread: Draft<Thread>,
  partType: 'text' | 'thinking',
  messageId: string,
  delta: string
): void {
  const msg = thread.messageTree.nodes[messageId];
  if (!msg) {
    return;
  }

  const part = findLastPartOfType(msg.content, partType);
  if (part) {
    part.text += delta;
  }
}

function handleStreamingEnd(
  thread: Draft<Thread>,
  partType: 'text' | 'thinking',
  messageId: string
): void {
  const msg = thread.messageTree.nodes[messageId];
  if (!msg) {
    return;
  }

  const part = findLastPartOfType(msg.content, partType);
  if (part) {
    part.status = 'complete';
  }
}
